// @flow

import React from "react";
import { localize } from "redux-i18n";
import { SimpleGrid, WistiaPlayer } from "@components";

type Props = PropsFromLocalize;

class HelpPanelAlerts extends React.Component<Props> {
  get criteriaData() {
    return [
      {
        id: "alert-name",
        label: this.props.t("ALERT_NAME"),
        details: this.props.t("ALERTS_HELP_NAME_DESCRIPTION"),
      },
      {
        id: "status",
        label: this.props.t("STATUS"),
        details: this.props.t("ALERTS_HELP_STATUS_DESCRIPTION"),
      },
      {
        id: "absence-types-covered",
        label: this.props.t("ALERTS_ALERT_ABSENCE_TYPES_COVERED"),
        details: this.props.t("ALERTS_HELP_ABSENCE_TYPE_DESCRIPTION"),
      },
      {
        id: "absence-purpose",
        label: this.props.t("ALERTS_ALERT_PURPOSE"),
        details: [
          {
            id: "absence-purpose-reference-description",
            content: this.props.t("ALERTS_HELP_REFERENCE_DESCRIPTION", {
              title: <span className="font-weight-bold">{this.props.t("REFERENCE_ALERT")}</span>,
            }),
          },
          {
            id: "absence-purpose-monitoring-description",
            content: this.props.t("ALERTS_HELP_MONITORING_DESCRIPTION", {
              title: <span className="font-weight-bold">{this.props.t("MONITORING_PROMPT")}</span>,
            }),
          },
          {
            id: "absence-purpose-wellbeing-description",
            content: this.props.t("ALERTS_HELP_WELLBEING_DESCRIPTION", {
              title: <span className="font-weight-bold">{this.props.t("WELLBEING_REFERRAL")}</span>,
            }),
          },
        ],
      },
      {
        id: "trigger-point",
        label: this.props.t("ALERTS_ALERT_TRIGGER_POINT"),
        details: [
          {
            id: "trigger-point-options-description",
            content: this.props.t("ALERTS_HELP_SETTINGS_CONTROL_OPTIONS_DESCRIPTION"),
          },
          {
            id: "trigger-point-onclose-description",
            content: this.props.t("ALERTS_HELP_SETTINGS_CONTROL_ONCLOSE_DESCRIPTION", {
              title: <span className="font-weight-bold">{this.props.t("ON_CLOSE")}</span>,
            }),
          },
          {
            id: "trigger-point-predicted-description",
            content: this.props.t("ALERTS_HELP_SETTINGS_CONTROL_PREDICTED_DESCRIPTION", {
              title: <span className="font-weight-bold">{this.props.t("PREDICTED")}</span>,
            }),
          },
          {
            id: "trigger-point-time-description",
            content: this.props.t("ALERTS_HELP_SETTINGS_CONTROL_TIME_DESCRIPTION", {
              title: <span className="font-weight-bold">{this.props.t("ACTUAL_TIME")}</span>,
            }),
          },
        ],
      },
      {
        id: "valid-from",
        label: this.props.t("ALERTS_ALERT_VALID_FROM"),
        details: this.props.t("ALERTS_HELP_VALID_FROM_DESCRIPTION"),
      },
      {
        id: "end-date",
        label: this.props.t("END_DATE"),
        details: this.props.t("ALERTS_HELP_END_DATE_DESCRIPTION"),
      },
      {
        id: "days",
        label: this.props.t("DAYS"),
        details: this.props.t("ALERTS_HELP_DAYS_DESCRIPTION"),
      },
      {
        id: "day-type",
        label: this.props.t("DAY_TYPE"),
        details: this.props.t("ALERTS_HELP_DAY_TYPE_DESCRIPTION"),
      },
      {
        id: "fte-pro-rata",
        label: this.props.t("ALERTS_ALERT_FTE_PRO_RATA_ADJUSTMENT"),
        details: this.props.t("ALERTS_HELP_FTE_DESCRIPTION"),
      },
    ];
  }

  get contentData() {
    return [
      {
        id: "instruction",
        label: this.props.t("INSTRUCTION"),
        details: this.props.t("ALERTS_HELP_INSTRUCTION_DESCRIPTION"),
      },
      {
        id: "fields",
        label: this.props.t("FIELDS"),
        details: this.props.t("ALERTS_HELP_FIELDS_DESCRIPTION"),
      },
      {
        id: "attachments",
        label: this.props.t("ATTACHMENTS"),
        details: this.props.t("ALERTS_HELP_ATTACHMENTS_DESCRIPTION"),
      },
    ];
  }

  get recipientsData() {
    return [
      {
        id: "default-recipients",
        label: this.props.t("DEFAULT_RECIPIENTS"),
        details: this.props.t("ALERTS_HELP_DEFAULT_RECIPIENTS_DESCRIPTION"),
      },
      {
        id: "bespoke-recipients",
        label: this.props.t("BESPOKE_RECIPIENTS"),
        details: this.props.t("ALERTS_HELP_BESPOKE_RECIPIENTS_DESCRIPTION"),
      },
    ];
  }

  get scopeData() {
    return [
      {
        id: "absence-reasons",
        label: this.props.t("ABSENCE_REASONS"),
        details: this.props.t("ALERTS_HELP_ABSENCE_REASONS_DESCRIPTION"),
      },
      {
        id: "staff-groups",
        label: this.props.t("STAFF_GROUPS"),
        details: this.props.t("ALERTS_HELP_STAFF_GROUPS_DESCRIPTION"),
      },
      {
        id: "service-length",
        label: this.props.t("SERVICE_LENGTH"),
        details: this.props.t("ALERTS_HELP_SERVICE_LENGTH_DESCRIPTION"),
      },
    ];
  }

  get columns() {
    return [
      {
        title: this.props.t("ITEM"),
        name: "setting",
        width: 110,
        renderItem: item => item.label,
      },
      {
        title: this.props.t("DESCRIPTION"),
        name: "details",
        renderItem: item => {
          if (typeof item.details === "string") {
            return item.details;
          }
          return (
            <div>
              {item.details.map(el => {
                return (
                  <div className="pt-1 pb-1" key={el.id}>
                    {el.content}
                  </div>
                );
              })}
            </div>
          );
        },
      },
    ];
  }

  render() {
    return (
      <div className="side-panel" onClick={this.props.onToggle}>
        <div className="d-flex justify-content-between align-items-center mb-2 border-bottom-color-dark-grey">
          <h3>{this.props.t("ALERT_TRIGGERS")}</h3>
          <span onClick={this.props.onToggle} className="material-icons fs-30 pointer">
            last_page
          </span>
        </div>
        <p className="fs-14">{this.props.t("ALERTS_HELP_INTRODUCTION")}</p>
        <h6 className="font-weight-bold">{this.props.t("TUTORIAL")}</h6>
        <div className="mb-3">
          <WistiaPlayer
            videoId={WistiaPlayer.videos.ALERTS_INSTRUCTIONS}
            title={this.props.t("ALERTS_INSTRUCTIONS")}
          />
        </div>
        <h6 className="font-weight-bold">{this.props.t("ALERT_CRITERIA")}</h6>
        <SimpleGrid className="theme-help" columns={this.columns} rows={this.criteriaData} />
        <h6 className="font-weight-bold">{this.props.t("ALERT_CONTENT")}</h6>
        <SimpleGrid className="theme-help" columns={this.columns} rows={this.contentData} />
        <h6 className="font-weight-bold">{this.props.t("ALERT_RECIPIENTS")}</h6>
        <SimpleGrid className="theme-help" columns={this.columns} rows={this.recipientsData} />
        <h6 className="font-weight-bold">{this.props.t("ALERT_SCOPE")}</h6>
        <SimpleGrid className="theme-help" columns={this.columns} rows={this.scopeData} />
      </div>
    );
  }
}

export default localize()(HelpPanelAlerts);
